import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';

export const getPicksViewStyles = (theme: Theme, windowWidth = 390) => {
  const wcWebMaxWidth = Platform.OS === 'web'
    ? (windowWidth >= 1024 ? 880 : windowWidth >= 768 ? 700 : 560)
    : undefined;
  const wcContentConstraint = wcWebMaxWidth != null
    ? { maxWidth: wcWebMaxWidth, width: '100%' as never, alignSelf: 'center' as const }
    : {};

  return StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.background.primary,
    },
    topStack: {
      paddingTop: 8,
    },
    /** Wraps an injected header (e.g. the self-profile stats block) above the futures card. */
    headerSlotWrap: {
      paddingHorizontal: 16,
      paddingBottom: 4,
      ...wcContentConstraint,
    },
    /** Wraps futures + tabs; live refresh overlays the top without changing layout height. */
    listHeaderBody: {
      position: 'relative',
      ...wcContentConstraint,
    },
    backgroundRefreshOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 8,
      backgroundColor: theme.background.primary,
      ...(Platform.OS === 'android' ? { elevation: 4 } : {}),
      ...(Platform.OS === 'web'
        ? ({ boxShadow: '0 6px 16px rgba(0,0,0,0.08)' } as never)
        : {
            shadowColor: '#000',
            shadowOffset: { width: 0, height: 4 },
            shadowOpacity: 0.08,
            shadowRadius: 8,
          }),
    },
    backgroundRefreshStrip: {
      height: 2,
      width: '100%',
      backgroundColor: theme.primary,
    },
    backgroundRefreshRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
      paddingHorizontal: 12,
      paddingVertical: 4,
    },
    backgroundRefreshLabel: {
      fontSize: 11,
      fontWeight: '600',
      color: theme.gray[600],
    },
    list: {
      flex: 1,
    },
    listContent: {
      paddingBottom: 120,
      ...wcContentConstraint,
    },
    pillsRow: {
      width: '94%',
      maxWidth: 520,
      alignSelf: 'center',
      marginBottom: 8,
    },
    sectionHeaderRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingHorizontal: 20,
      paddingTop: 22,
      paddingBottom: 2,
      backgroundColor: theme.background.primary,
      ...wcContentConstraint,
    },
    sectionHeaderAccent: {
      width: 5,
      height: 5,
      borderRadius: 3,
      backgroundColor: theme.primary,
    },
    sectionHeader: {
      fontSize: 10,
      fontWeight: '700',
      color: theme.gray[500],
      letterSpacing: 1.5,
      textTransform: 'uppercase',
    },
    dateHeader: {
      paddingHorizontal: 20,
      paddingTop: 8,
      paddingBottom: 4,
      backgroundColor: theme.background.primary,
      ...wcContentConstraint,
    },
    dateHeaderText: {
      fontSize: 16,
      fontWeight: '800',
      color: theme.gray[800],
      letterSpacing: -0.3,
    },
    listEmptyWrap: {
      alignItems: 'center',
      paddingHorizontal: 32,
      paddingTop: 40,
      paddingBottom: 24,
    },
    listEmptyTitle: {
      fontSize: 16,
      fontWeight: '700',
      color: theme.gray[700],
      marginBottom: 8,
      textAlign: 'center',
    },
    listEmptySubtitle: {
      fontSize: 14,
      color: theme.gray[400],
      textAlign: 'center',
      lineHeight: 20,
    },
    emptyIcon: {
      fontSize: 48,
      marginBottom: 14,
    },
    emptyMonkey: {
      width: 100,
      height: 100,
      marginBottom: 16,
    },
    errorContainer: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: 32,
      paddingBottom: 48,
    },
    errorIconWrap: {
      marginBottom: 16,
    },
    errorTitle: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.gray[700],
      marginBottom: 8,
      textAlign: 'center',
    },
    errorSubtitle: {
      fontSize: 14,
      color: theme.gray[400],
      textAlign: 'center',
      lineHeight: 20,
      marginBottom: 24,
    },
    errorButton: {
      paddingHorizontal: 24,
      paddingVertical: 10,
      borderRadius: 20,
      backgroundColor: theme.primary,
    },
    errorButtonLabel: {
      fontSize: 14,
      fontWeight: '700',
      color: theme.onPrimary,
    },
  });
};
